import { Redirect, router } from 'expo-router';
import { Eye, EyeOff } from 'lucide-react-native';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { Button } from '@/src/components/Button';
import { Input } from '@/src/components/Input';
import { LoadingView } from '@/src/components/LoadingView';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import {
  emailChangeSchema,
  type EmailChangeValues,
} from '@/src/features/auth/validation';
import { useAuth } from '@/src/features/auth/use-auth';
import { hasCompletedOnboarding } from '@/src/features/profile/profile-service';
import { useProfile } from '@/src/features/profile/use-profile';
import { useAppStore } from '@/src/state/app-store';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { UserFacingError } from '@/src/utils/errors';

export default function VerifyEmailScreen() {
  const {
    refreshCurrentUser,
    requestEmailChange,
    sendVerificationEmail,
    signOut,
    user,
  } = useAuth();
  const { showError, showSuccess } = useToast();
  const profileQuery = useProfile(user?.uid);
  const pendingEmailVerificationRegistration = useAppStore(
    (state) => state.pendingEmailVerificationRegistration,
  );
  const setPendingEmailVerificationRegistration = useAppStore(
    (state) => state.setPendingEmailVerificationRegistration,
  );
  const [checking, setChecking] = useState(false);
  const [currentPasswordVisible, setCurrentPasswordVisible] = useState(false);
  const [resending, setResending] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const {
    control: emailChangeControl,
    formState: {
      errors: emailChangeErrors,
      isSubmitting: isSubmittingEmailChange,
    },
    handleSubmit: handleEmailChangeSubmit,
    reset: resetEmailChange,
    setError: setEmailChangeError,
  } = useForm<EmailChangeValues>({
    defaultValues: {
      currentPassword: '',
      newEmail: '',
    },
  });

  if (!user) {
    return <Redirect href="/sign-in" />;
  }

  if (user.emailVerified) {
    if (profileQuery.isLoading || profileQuery.isFetching) {
      return <LoadingView message="Checking account status..." />;
    }

    return (
      <Redirect
        href={
          profileQuery.data
            ? hasCompletedOnboarding(profileQuery.data)
              ? '/verify-account'
              : '/onboarding'
            : '/onboarding'
        }
      />
    );
  }

  async function handleCheckVerification() {
    setChecking(true);

    try {
      const refreshedUser = await refreshCurrentUser();

      if (refreshedUser?.emailVerified) {
        const refreshedProfile = await profileQuery.refetch();
        const profile = refreshedProfile.data ?? profileQuery.data;

        showSuccess('Your email is verified.', 'Email confirmed');
        router.replace(
          profile
            ? hasCompletedOnboarding(profile)
              ? '/verify-account'
              : '/onboarding'
            : '/onboarding',
        );
        return;
      }

      showError(
        new UserFacingError('Your email is not verified yet.'),
        'Still waiting for confirmation.',
      );
    } catch (error) {
      showError(error, 'Unable to check email verification.');
    } finally {
      setChecking(false);
    }
  }

  async function handleResend() {
    setResending(true);

    try {
      await sendVerificationEmail('signup');
      showSuccess(
        'Check your email for a new verification link.',
        'Email sent',
      );
    } catch (error) {
      showError(error, 'Unable to send verification email.');
    } finally {
      setResending(false);
    }
  }

  async function handleSignOut() {
    setSigningOut(true);

    try {
      await signOut();
      router.replace('/sign-in');
    } catch (error) {
      showError(error, 'Unable to sign out.');
    } finally {
      setSigningOut(false);
    }
  }

  const handleEmailChange = handleEmailChangeSubmit(async (values) => {
    const parsed = emailChangeSchema.safeParse(values);

    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        const field = issue.path[0];

        if (field === 'newEmail' || field === 'currentPassword') {
          setEmailChangeError(field, { message: issue.message });
        }
      });
      return;
    }

    if (
      user?.email &&
      parsed.data.newEmail.toLowerCase() === user.email.toLowerCase()
    ) {
      setEmailChangeError('newEmail', {
        message: 'Enter a different email address.',
      });
      return;
    }

    try {
      await requestEmailChange(
        parsed.data.newEmail,
        parsed.data.currentPassword,
        'signup',
      );

      if (pendingEmailVerificationRegistration?.uid === user?.uid) {
        setPendingEmailVerificationRegistration({
          ...pendingEmailVerificationRegistration,
          email: parsed.data.newEmail,
        });
      }

      resetEmailChange();
      showSuccess(
        'Check the new email address for a confirmation link.',
        'Email change sent',
      );
    } catch (error) {
      showError(error, 'Unable to send verification to that email.');
    }
  });

  return (
    <Screen
      title="Verify your email"
      subtitle="Confirm your email before activating your Frienzzi account."
    >
      <View style={styles.panel}>
        <Text style={styles.body}>
          We sent a verification link to your email address. Open that email and
          confirm it, then come back here to continue.
        </Text>
        {user.email ? <Text style={styles.email}>{user.email}</Text> : null}
        <View style={styles.actions}>
          <Button
            title="I verified my email"
            loading={checking}
            onPress={handleCheckVerification}
          />
          <Button
            title="Resend verification email"
            variant="secondary"
            loading={resending}
            onPress={handleResend}
          />
          <Button
            title="Sign out"
            variant="secondary"
            loading={signingOut}
            onPress={handleSignOut}
          />
        </View>
      </View>
      <View style={styles.panel}>
        <Text style={styles.sectionTitle}>Wrong email?</Text>
        <Text style={styles.body}>
          Enter the correct email address and your current password. We will
          send a new confirmation link to that address.
        </Text>
        <View style={styles.emailChangeForm}>
          <Controller
            control={emailChangeControl}
            name="newEmail"
            render={({ field: { onBlur, onChange, value } }) => (
              <Input
                label="New email"
                autoCapitalize="none"
                autoComplete="email"
                keyboardType="email-address"
                onBlur={onBlur}
                onChangeText={onChange}
                textContentType="emailAddress"
                value={value}
                error={emailChangeErrors.newEmail?.message}
              />
            )}
          />
          <Controller
            control={emailChangeControl}
            name="currentPassword"
            render={({ field: { onBlur, onChange, value } }) => (
              <Input
                label="Current password"
                autoComplete="current-password"
                onBlur={onBlur}
                onChangeText={onChange}
                secureTextEntry={!currentPasswordVisible}
                textContentType="password"
                value={value}
                error={emailChangeErrors.currentPassword?.message}
                rightAccessory={
                  <Pressable
                    accessibilityLabel={
                      currentPasswordVisible ? 'Hide password' : 'Show password'
                    }
                    accessibilityRole="button"
                    onPress={() =>
                      setCurrentPasswordVisible((visible) => !visible)
                    }
                    style={styles.passwordToggle}
                  >
                    {currentPasswordVisible ? (
                      <EyeOff
                        color={colors.secondary}
                        size={20}
                        strokeWidth={2.4}
                      />
                    ) : (
                      <Eye
                        color={colors.secondary}
                        size={20}
                        strokeWidth={2.4}
                      />
                    )}
                  </Pressable>
                }
              />
            )}
          />
          <Button
            title="Send verification to new email"
            loading={isSubmittingEmailChange}
            onPress={handleEmailChange}
          />
        </View>
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  actions: {
    gap: spacing.sm,
  },
  body: {
    color: colors.text,
    fontSize: 16,
    lineHeight: 23,
  },
  email: {
    color: colors.primary,
    fontSize: 18,
    fontWeight: '900',
  },
  emailChangeForm: {
    gap: spacing.md,
  },
  panel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.lg,
    padding: spacing.lg,
  },
  passwordToggle: {
    alignItems: 'center',
    borderRadius: 999,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  sectionTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
});
